export const DIMENSION_AXES = ['width', 'length', 'height', 'diameter'] as const
const DIMENSION_DISPLAY_AXES = [
  'length',
  'width',
  'height',
  'diameter',
] as const

export type DimensionAxis = (typeof DIMENSION_AXES)[number]
export type DimensionFieldKey = `dimension.${DimensionAxis}`

export type DimensionRangeItem = {
  key: DimensionAxis
  min?: number | string
  max?: number | string
}

export type DimensionSource = Record<string, unknown> & {
  dimension?: unknown
  dimensionRange?: unknown
}

export type ParsedDimensionInput =
  | { kind: 'empty' }
  | { kind: 'scalar'; value: number }
  | { kind: 'range'; min: number; max: number }
  | { kind: 'invalid'; raw: string }

export type FormattedDimensionLabels = {
  numbers: string
  labels: string
  label: string
  raw: {
    dimension?: Record<string, unknown>
    dimensionRange?: DimensionRangeItem[]
  }
}

export const DIMENSION_INPUT_FALLBACK_MESSAGE =
  'Valeur attendue: nombre ou plage min-max'

const AXIS_LABELS: Record<DimensionAxis, string> = {
  width: 'largeur',
  length: 'longueur',
  height: 'hauteur',
  diameter: 'diamètre',
}

const AXIS_PLACEHOLDERS: Record<DimensionAxis, string> = {
  length: 'L',
  width: 'l',
  height: 'h',
  diameter: 'diam',
}

const unwrapInputValue = (
  value: unknown,
  options?: { preferLabel?: boolean },
): unknown => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    if (options?.preferLabel) {
      if (record.label !== undefined) {
        return unwrapInputValue(record.label, options)
      }
      if (record.value !== undefined) {
        return unwrapInputValue(record.value, options)
      }
    } else {
      if (record.value !== undefined) {
        return unwrapInputValue(record.value, options)
      }
      if (record.label !== undefined) {
        return unwrapInputValue(record.label, options)
      }
    }
  }

  return value
}

const formatInvalidDimensionRaw = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return String(value)
  return ''
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const toNumber = (value: unknown): number | null => {
  const unwrapped = unwrapInputValue(value)
  if (typeof unwrapped === 'number') {
    return Number.isFinite(unwrapped) && unwrapped >= 0 ? unwrapped : null
  }

  if (typeof unwrapped !== 'string') return null
  const trimmed = unwrapped.trim()
  if (trimmed.length === 0) return null

  const normalized = trimmed.replace(/\s+/g, '').replace(',', '.')
  const parsed = Number(normalized)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

const formatNumberForInput = (value: number): string =>
  String(value).replace('.', ',')

const formatNumberForDisplay = (value: number): string =>
  value.toLocaleString('fr-FR')

const toDimensionValueString = (
  parsed: Extract<ParsedDimensionInput, { kind: 'scalar' | 'range' }>,
  formatter: (value: number) => string,
): string => {
  if (parsed.kind === 'scalar') return formatter(parsed.value)
  return `${formatter(parsed.min)}-${formatter(parsed.max)}`
}

const getDimensionEntry = (
  source: DimensionSource | undefined,
  axis: DimensionAxis,
): unknown => {
  if (!source) return undefined

  const dottedKey = `dimension.${axis}`
  if (source[dottedKey] !== undefined) {
    return source[dottedKey]
  }

  if (isRecord(source.dimension) && source.dimension[axis] !== undefined) {
    return source.dimension[axis]
  }

  return undefined
}

const getDimensionUnitEntry = (
  source: DimensionSource | undefined,
): unknown => {
  if (!source) return undefined

  if (source['dimension.unit'] !== undefined) {
    return source['dimension.unit']
  }

  if (isRecord(source.dimension) && source.dimension.unit !== undefined) {
    return source.dimension.unit
  }

  return undefined
}

const normalizeDimensionRangeItem = (
  item: unknown,
): DimensionRangeItem | undefined => {
  if (!isRecord(item) || typeof item.key !== 'string') return undefined
  if (!isDimensionAxis(item.key)) return undefined

  const min = toNumber(item.min)
  const max = toNumber(item.max)
  if (min === null || max === null || min > max) return undefined

  return {
    key: item.key,
    min,
    max,
  }
}

export const isDimensionAxis = (value: string): value is DimensionAxis =>
  DIMENSION_AXES.includes(value as DimensionAxis)

export const resolveCanonicalDimensionAxis = (
  value: string,
): DimensionAxis | null => {
  const normalized = value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
  if (isDimensionAxis(normalized)) return normalized
  const frenchLabels: Record<string, DimensionAxis> = {
    largeur: 'width',
    hauteur: 'height',
    longueur: 'length',
    diametre: 'diameter',
    'haut./epaiss.': 'height',
  }
  return Object.hasOwn(frenchLabels, normalized)
    ? frenchLabels[normalized]
    : null
}

export const getDimensionAxisFromField = (
  key: string,
): DimensionAxis | null => {
  const match = /^dimension\.(width|length|height|diameter)$/.exec(key)
  if (!match?.[1] || !isDimensionAxis(match[1])) return null
  return match[1]
}

export const isDimensionAxisField = (key: string): key is DimensionFieldKey =>
  getDimensionAxisFromField(key) !== null

export const getDimensionFieldPlaceholder = (axis: DimensionAxis): string =>
  AXIS_PLACEHOLDERS[axis]

export const cloneDimensionRangeItems = (
  value: unknown,
): DimensionRangeItem[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => normalizeDimensionRangeItem(item))
    .filter((item): item is DimensionRangeItem => item !== undefined)
}

export const parseDimensionInput = (value: unknown): ParsedDimensionInput => {
  const unwrapped = unwrapInputValue(value)

  if (unwrapped === null || unwrapped === undefined) {
    return { kind: 'empty' }
  }

  if (typeof unwrapped === 'number') {
    return Number.isFinite(unwrapped) && unwrapped >= 0
      ? { kind: 'scalar', value: unwrapped }
      : { kind: 'invalid', raw: formatInvalidDimensionRaw(unwrapped) }
  }

  if (typeof unwrapped !== 'string') {
    return { kind: 'invalid', raw: formatInvalidDimensionRaw(unwrapped) }
  }

  const trimmed = unwrapped.trim()
  if (trimmed.length === 0) return { kind: 'empty' }

  const rangeMatch = /^(\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)$/.exec(trimmed)
  if (rangeMatch) {
    const min = toNumber(rangeMatch[1])
    const max = toNumber(rangeMatch[2])

    if (min === null || max === null || min > max) {
      return { kind: 'invalid', raw: trimmed }
    }

    return { kind: 'range', min, max }
  }

  const scalar = toNumber(trimmed)
  if (scalar !== null) {
    return { kind: 'scalar', value: scalar }
  }

  return { kind: 'invalid', raw: trimmed }
}

export const readDimensionInput = (
  source: DimensionSource | undefined,
  axis: DimensionAxis,
): ParsedDimensionInput => {
  const dottedKey = `dimension.${axis}`
  if (source && source[dottedKey] !== undefined) {
    return parseDimensionInput(source[dottedKey])
  }

  const rangeItem = cloneDimensionRangeItems(source?.dimensionRange).find(
    (item) => item.key === axis,
  )

  if (
    rangeItem &&
    typeof rangeItem.min === 'number' &&
    typeof rangeItem.max === 'number'
  ) {
    return {
      kind: 'range',
      min: rangeItem.min,
      max: rangeItem.max,
    }
  }

  return parseDimensionInput(getDimensionEntry(source, axis))
}

export const getDimensionInputValue = (
  source: DimensionSource | undefined,
  axis: DimensionAxis,
): string => {
  const parsed = readDimensionInput(source, axis)
  if (parsed.kind === 'empty') return ''
  if (parsed.kind === 'invalid') return parsed.raw
  return toDimensionValueString(parsed, formatNumberForInput)
}

export const getDimensionDisplayValue = (
  source: DimensionSource | undefined,
  axis: DimensionAxis,
): string => {
  const parsed = readDimensionInput(source, axis)
  if (parsed.kind === 'empty') return ''
  if (parsed.kind === 'invalid') return parsed.raw
  return toDimensionValueString(parsed, formatNumberForDisplay)
}

export const getDimensionSortTuple = (
  value: unknown,
): { empty: boolean; min: number; max: number } => {
  const parsed = parseDimensionInput(value)
  if (parsed.kind === 'scalar') {
    return { empty: false, min: parsed.value, max: parsed.value }
  }

  if (parsed.kind === 'range') {
    return { empty: false, min: parsed.min, max: parsed.max }
  }

  return {
    empty: true,
    min: Number.POSITIVE_INFINITY,
    max: Number.POSITIVE_INFINITY,
  }
}

export const getDimensionUnit = (
  source: DimensionSource | undefined,
): string | undefined => {
  const value = unwrapInputValue(getDimensionUnitEntry(source), {
    preferLabel: true,
  })
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

export const formatDimensionSummary = (
  source: DimensionSource | undefined,
): FormattedDimensionLabels | null => {
  if (!source) return null

  const segments = DIMENSION_DISPLAY_AXES.flatMap((axis) => {
    const parsed = readDimensionInput(source, axis)
    if (parsed.kind === 'empty' || parsed.kind === 'invalid') return []

    const value = toDimensionValueString(parsed, formatNumberForDisplay)
    return [
      {
        axis,
        label: AXIS_LABELS[axis],
        value: axis === 'diameter' ? `(diam.) ${value}` : value,
      },
    ]
  })

  if (segments.length === 0) return null

  const unit = getDimensionUnit(source)
  const numbers = segments.map((segment) => segment.value).join(' x ')
  const labels = segments.map((segment) => segment.label).join(' x ')

  return {
    numbers,
    labels,
    label: unit ? `${numbers} ${unit}` : numbers,
    raw: {
      dimension: isRecord(source.dimension)
        ? { ...source.dimension }
        : undefined,
      dimensionRange: cloneDimensionRangeItems(source.dimensionRange),
    },
  }
}

/**
 * Applies a dimension input directly to the provided target.
 * The function is intentionally mutable: it replaces `target.dimension`
 * and `target.dimensionRange` with normalized copies for the edited axis.
 * Invalid inputs leave the target unchanged.
 */
export const applyDimensionInput = (
  target: DimensionSource,
  axis: DimensionAxis,
  value: unknown,
): ParsedDimensionInput => {
  const parsed = parseDimensionInput(value)
  if (parsed.kind === 'invalid') return parsed

  const nextDimension = isRecord(target.dimension)
    ? { ...target.dimension }
    : {}
  const nextRanges = cloneDimensionRangeItems(target.dimensionRange).filter(
    (item) => item.key !== axis,
  )

  if (parsed.kind === 'scalar') {
    nextDimension[axis] = parsed.value
  } else {
    nextDimension[axis] = null
  }

  if (parsed.kind === 'range') {
    nextRanges.push({
      key: axis,
      min: parsed.min,
      max: parsed.max,
    })
  }

  target.dimension = nextDimension
  target.dimensionRange = nextRanges

  return parsed
}
